import { CLIENT_TYPES, MATCH_SOURCES, PUSH_PROVIDERS, TRIGGER_TYPES, TV2M_PUSH_TYPE, } from '@apm/contracts';
import { resolveNotificationCopy } from './notification-copy.js';
/**
 * Push payload 파싱. **우리 알림이 아닌 것을 걸러내는 유일한 근거가 `type: 'tv2m'` 이다**
 * (`docs/08` §4). 같은 Service Worker 가 다른 목적의 알림도 받을 수 있으므로, 판별에
 * 실패한 것을 우리 것으로 다루면 남의 알림을 우리 딥링크로 열어 버린다.
 *
 * 필드명이 snake_case 인 것은 계약이다 (CLAUDE.md 절대 규칙 11) — v7 문서 §6.1 과 1:1 이라
 * 네이티브 포팅 시 이 파서를 그대로 옮길 수 있다.
 */
const MATCH_MODES = ['server_side', 'client_fallback', 'same_device'];
const NOTIFICATION_MODES = ['silent', 'direct'];
/**
 * 우리 payload 인가. **파싱에 성공하는지까지 본다.**
 *
 * `type` 만 보면 필수 필드가 빠진 payload 를 우리 것으로 판정하고, 그 뒤에 이어지는
 * `handleTv2mPush` 가 `null` 을 돌려준다 — 호출부는 "우리 알림인데 처리에 실패했다" 로
 * 읽고 오류 화면을 띄운다. 두 판정이 갈리지 않게 한쪽이 다른 쪽을 부른다.
 */
export function isTv2mPush(data) {
    return parseTv2mPushPayload(data) !== null;
}
/**
 * payload 를 되읽는다. 우리 것이 아니거나 필수 필드가 어긋나면 `null`.
 *
 * **입력이 문자열일 수 있다.** Service Worker 의 `event.data` 는 `json()` 과 `text()` 를
 * 모두 주는데, 발송처나 브라우저에 따라 `json()` 이 던지는 경우가 있어 호출부가 `text()` 로
 * 떨어질 수 있다. 어느 쪽으로 와도 받는다 — 그 분기를 화면마다 다시 쓰게 두지 않는다.
 *
 * **필수와 선택의 처리가 다르다.** 필수 필드가 어긋나면 전체를 버리고, 선택 필드가
 * 어긋나면 **그 필드만 빼고 나머지를 살린다.** 쿠폰 코드 하나 때문에 알림을 통째로
 * 놓치는 것이 더 나쁘다 — 알림은 시청자가 TV 앞에서 기다리는 것이다.
 */
export function parseTv2mPushPayload(data) {
    const raw = toRecord(data);
    if (!raw)
        return null;
    if (raw['type'] !== TV2M_PUSH_TYPE)
        return null;
    const matchId = str(raw['match_id']);
    const campaignId = str(raw['campaign_id']);
    const productId = str(raw['product_id']);
    const deepLinkUrl = str(raw['deep_link_url']);
    const expiresAt = str(raw['expires_at']);
    const sentAt = str(raw['sent_at']);
    if (!matchId || !campaignId || !productId || !deepLinkUrl || !expiresAt || !sentAt)
        return null;
    const triggerType = oneOf(raw['trigger_type'], TRIGGER_TYPES);
    const matchMode = oneOf(raw['match_mode'], MATCH_MODES);
    const matchSource = oneOf(raw['match_source'], MATCH_SOURCES);
    const clientType = oneOf(raw['client_type'], CLIENT_TYPES);
    const pushProvider = oneOf(raw['push_provider'], PUSH_PROVIDERS);
    if (!triggerType || !matchMode || !matchSource || !clientType || !pushProvider)
        return null;
    // `advertiser_id` 는 필수지만 값이 `null` 인 것이 정상이다 (광고주 없는 편성 유입).
    // 키 자체가 없는 것도 `null` 로 받는다 — 없는 것과 비어 있는 것의 결말이 같다.
    const advertiserId = nullableStr(raw['advertiser_id']);
    if (advertiserId === undefined)
        return null;
    // 제목·본문은 필수 필드이지만 여기서 막지 않는다. 비어 있으면 폴백 문구가 있고
    // (`notification-copy.ts`), 문구 하나 때문에 알림을 버릴 이유가 없다.
    const payload = {
        type: TV2M_PUSH_TYPE,
        match_id: matchId,
        campaign_id: campaignId,
        advertiser_id: advertiserId,
        product_id: productId,
        trigger_type: triggerType,
        deep_link_url: deepLinkUrl,
        title: str(raw['title']) ?? '',
        body: str(raw['body']) ?? '',
        expires_at: expiresAt,
        sent_at: sentAt,
        match_mode: matchMode,
        match_source: matchSource,
        client_type: clientType,
        push_provider: pushProvider,
    };
    // 선택 필드는 값이 성립할 때만 싣는다. 서버도 빈 값의 키 자체를 싣지 않는다
    // (`tv2m-server-sdk/core/payload.ts` — `channel_id: null` 은 "채널이 없다" 가 아니라 "id 가 null" 로 읽힌다).
    const channelId = str(raw['channel_id']);
    const contentId = str(raw['content_id']);
    const imageUrl = str(raw['image_url']);
    const notificationMode = oneOf(raw['notification_mode'], NOTIFICATION_MODES);
    const couponCode = nullableStr(raw['coupon_code']);
    const handoffChannelId = nullableStr(raw['handoff_channel_id']);
    const storeUrl = nullableStr(raw['store_url']);
    if (channelId)
        payload.channel_id = channelId;
    if (contentId)
        payload.content_id = contentId;
    if (imageUrl)
        payload.image_url = imageUrl;
    if (notificationMode)
        payload.notification_mode = notificationMode;
    if (couponCode !== undefined)
        payload.coupon_code = couponCode;
    if (handoffChannelId !== undefined)
        payload.handoff_channel_id = handoffChannelId;
    if (storeUrl !== undefined)
        payload.store_url = storeUrl;
    // **0 이 값이다.** truthy 로 거르면 재생 시작 직후의 트리거에서 위치가 사라진다
    // (서버가 같은 이유로 `!== undefined` 로 싣는다).
    const positionSec = raw['position_sec'];
    if (typeof positionSec === 'number' && Number.isFinite(positionSec)) {
        payload.position_sec = positionSec;
    }
    return payload;
}
/**
 * 알림을 화면에 띄울 것인가.
 *
 * `notification_mode: 'silent'` 는 payload 만 전달하고 알림은 띄우지 않는 모드다.
 * 값이 없으면 띄운다 — 선택 필드가 빠졌다고 알림이 조용히 사라지면, 시청자에게는
 * 매칭이 실패한 것과 구분되지 않는다.
 *
 * **`match_mode` 로는 분기하지 않는다** (WB-02). 웹 프로파일은 Client-side IP 매칭을
 * 시도할 수 없으므로(WB-06) `client_fallback` 이 와도 검증할 수단이 없고, 서버가 이미
 * 내린 판단을 클라이언트가 뒤집을 근거가 없다. 그대로 신뢰해 표시한다.
 */
export function shouldDisplayNotification(payload) {
    return payload.notification_mode !== 'silent';
}
/**
 * 이미 만료된 payload 인가. 발송처가 오래 붙들고 있다가 전달하는 경우가 있다.
 *
 * 판정만 하고 **버리지 않는다.** 만료된 알림을 띄울지는 화면의 결정이고, 여기서 버리면
 * 그 사실이 관측되지 않아 "왜 알림이 안 왔는가" 를 되짚을 수 없다.
 */
export function isExpired(payload, nowMs) {
    const expiresAt = Date.parse(payload.expires_at);
    return Number.isFinite(expiresAt) && expiresAt <= nowMs;
}
/**
 * 알림 사양을 만든다.
 *
 * `tag` 를 `match_id` 로 둔다. 같은 매칭의 알림이 재전송으로 두 번 도착해도 화면에는
 * 하나만 남는다 — 재시도가 최대 4회인 발송 정책(T4)에서 실제로 일어날 수 있는 일이다.
 */
export function buildNotificationSpec(payload) {
    const copy = resolveNotificationCopy(payload);
    const spec = {
        title: copy.title,
        body: copy.body,
        deepLinkUrl: payload.deep_link_url,
        tag: payload.match_id,
    };
    if (payload.image_url)
        spec.imageUrl = payload.image_url;
    return spec;
}
// ── 파싱 도우미 ────────────────────────────────────────────────
/** 객체이거나, JSON 문자열이면 그것을 푼 객체. 배열은 payload 가 아니다. */
function toRecord(data) {
    if (typeof data === 'string') {
        try {
            return toRecord(JSON.parse(data));
        }
        catch {
            return null;
        }
    }
    if (typeof data !== 'object' || data === null || Array.isArray(data))
        return null;
    return data;
}
/** 비지 않은 문자열이면 그것을, 아니면 `undefined`. */
function str(value) {
    return typeof value === 'string' && value !== '' ? value : undefined;
}
/**
 * `string | null` 자리. 값이 어긋나면 `undefined` 를 준다 —
 * 호출부가 "없음(null)" 과 "형식이 틀림(undefined)" 을 구분할 수 있어야 한다.
 */
function nullableStr(value) {
    if (value === null || value === undefined)
        return null;
    return typeof value === 'string' ? value : undefined;
}
/** 열거 배열 안의 값이면 그것을, 아니면 `undefined`. */
function oneOf(value, allowed) {
    return typeof value === 'string' && allowed.includes(value)
        ? value
        : undefined;
}
//# sourceMappingURL=push-payload.js.map